// ============================================================================
// USA TAX CALCULATION - Federal Income Tax 2024
// Source: IRS Tax Tables 2024
// ============================================================================

import { TaxCalculationResult, CountryTaxData, DeductionBreakdown } from "./types";
import { calculateTaxByBrackets } from "./utils";

export const USA_TAX_CONFIG: CountryTaxData = {
  socialSecurityRate: 7.65, // FICA (Social Security 6.2% + Medicare 1.45%)
  socialSecurityMaxBase: 168600, // 2024 Social Security wage base
  taxBrackets: [
    { min: 0, max: 11600, rate: 10 },
    { min: 11600, max: 47150, rate: 12 },
    { min: 47150, max: 100525, rate: 22 },
    { min: 100525, max: 191950, rate: 24 },
    { min: 191950, max: 243725, rate: 32 },
    { min: 243725, max: 609350, rate: 35 },
    { min: 609350, max: Infinity, rate: 37 },
  ],
  personalAllowance: 14600, // Standard deduction 2024 (single)
  marriedAllowance: 29200, // Standard deduction 2024 (married filing jointly)
  childAllowances: [2000, 2000, 2000], // Child tax credit per child
  currency: "USD",
};

export interface USATaxParams {
  grossIncome: number;
  familyStatus: string;
  dependents: number;
}

/**
 * Calcula el impuesto federal de EEUU
 * 
 * Incluye:
 * - Federal Income Tax (tramos progresivos)
 * - FICA (Social Security 6.2% + Medicare 1.45%)
 * - Standard Deduction
 * - Child Tax Credit
 */
export function calculateUSATax({
  grossIncome,
  familyStatus,
  dependents,
}: USATaxParams): TaxCalculationResult {
  const config = USA_TAX_CONFIG;

  // Social Security (FICA)
  // Social Security only applies up to wage base, Medicare applies to all income
  const socialSecurityBase = Math.min(grossIncome, config.socialSecurityMaxBase!);
  const socialSecurityTax = (socialSecurityBase * 6.2) / 100; // Social Security
  const medicareTax = (grossIncome * 1.45) / 100; // Medicare
  const socialSecurity = socialSecurityTax + medicareTax;

  // Standard Deduction
  const standardDeduction = familyStatus === "married" 
    ? config.marriedAllowance 
    : config.personalAllowance;

  // Child Tax Credit (reduces tax, not income)
  let childTaxCredit = 0;
  for (let i = 0; i < Math.min(dependents, config.childAllowances.length); i++) {
    childTaxCredit += config.childAllowances[i];
  }

  // Taxable Income
  const taxableIncome = Math.max(0, grossIncome - standardDeduction);

  // Federal Income Tax
  const federalTax = calculateTaxByBrackets(taxableIncome, config.taxBrackets);

  // Apply Child Tax Credit (cannot reduce tax below zero)
  const finalTax = Math.max(0, federalTax - childTaxCredit);

  const taxRate = grossIncome > 0 ? (finalTax / grossIncome) * 100 : 0;

  // Build breakdown with US labels
  const breakdown: DeductionBreakdown[] = [];

  if (finalTax > 0) {
    breakdown.push({
      id: "federal_income_tax",
      amount: finalTax,
      rate: taxRate,
      type: "tax",
    });
  }

  if (socialSecurityTax > 0) {
    breakdown.push({
      id: "social_security",
      amount: socialSecurityTax,
      rate: grossIncome > 0 ? (socialSecurityTax / grossIncome) * 100 : 0,
      type: "social",
    });
  }

  if (medicareTax > 0) {
    breakdown.push({
      id: "medicare",
      amount: medicareTax,
      rate: grossIncome > 0 ? (medicareTax / grossIncome) * 100 : 0,
      type: "social",
    });
  }

  return {
    tax: finalTax,
    taxRate,
    socialSecurity,
    allowances: standardDeduction,
    breakdown,
  };
}
